import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { officeInfo } from '../data/mock';

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(t('contactSuccess'), {
          duration: 4000,
          position: 'top-center'
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(language === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'Error occurred, please try again', {
          duration: 4000,
          position: 'top-center'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(language === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'Error occurred, please try again', {
        duration: 4000,
        position: 'top-center'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t('language') === 'ar' ? 'الهاتف' : 'Phone',
      content: officeInfo.phone,
      link: `tel:${officeInfo.phone}`
    },
    {
      icon: MessageCircle,
      title: t('language') === 'ar' ? 'واتساب' : 'WhatsApp',
      content: '+971 50 206 0230',
      link: 'https://wa.me/971502060230'
    },
    {
      icon: Mail,
      title: t('language') === 'ar' ? 'البريد الإلكتروني' : 'Email',
      content: officeInfo.email,
      link: `mailto:${officeInfo.email}`
    },
    {
      icon: MapPin,
      title: t('language') === 'ar' ? 'العنوان' : 'Address',
      content: t('language') === 'ar' 
        ? 'كورنيش بلازا 1 - الطابق 3 - مكتب 302 - شارع الكورنيش - المجاز 1 - الشارقة - الإمارات'
        : officeInfo.address,
      link: officeInfo.mapUrl
    },
    {
      icon: Clock,
      title: t('language') === 'ar' ? 'ساعات العمل' : 'Working Hours',
      content: officeInfo.hours[language],
      link: null
    }
  ];

  return (
    <section id="contact" className="relative py-24 bg-white">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-100/40 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-amber-100 border border-amber-300 rounded-full text-amber-700 text-sm font-medium">
              {t('contact')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t('contactTitle')}
          </h2>
          <p className="text-lg text-slate-600">
            {t('contactSubtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 max-w-7xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const isPhoneOrEmailOrWhatsApp = info.icon === Phone || info.icon === Mail || info.icon === MessageCircle;
              const content = info.link ? (
                <a 
                  href={info.link}
                  target={info.icon === MapPin || info.icon === MessageCircle ? '_blank' : undefined}
                  rel={info.icon === MapPin || info.icon === MessageCircle ? 'noopener noreferrer' : undefined}
                  className="block hover:text-amber-600 transition-colors"
                  dir={isPhoneOrEmailOrWhatsApp ? 'ltr' : undefined}
                >
                  {info.content}
                </a>
              ) : (
                <span>{info.content}</span>
              );

              return (
                <div
                  key={index}
                  className="group bg-white border-2 border-amber-200 rounded-xl p-6 hover:border-amber-400 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-slate-900 font-semibold mb-2">{info.title}</h4>
                      <div className="text-slate-600 text-sm leading-relaxed">
                        {content}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Map */}
            <div className="rounded-xl overflow-hidden border-2 border-amber-200 shadow-xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3605.8786374397!2d55.38584!3d25.329556!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5bc20fa5c713%3A0x740436aae7fca3ad!2z2YXZg9iq2Kgg2YHYp9i32YXYqSDYudio2K_Yp9mE2YLYr9mI2LMg2KfZhNi52KjZitiv2YTZiiDZhNmE2YXYrdin2YXYp9ip!5e0!3m2!1sen!2sae!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale-0 hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white border-2 border-amber-200 rounded-2xl p-8 space-y-6 shadow-xl">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-slate-900 font-medium text-sm">
                    {t('contactName')}
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-white border-amber-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 h-12"
                    placeholder={t('language') === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-slate-900 font-medium text-sm">
                    {t('contactEmail')}
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-white border-amber-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 h-12"
                    placeholder={t('language') === 'ar' ? 'example@email.com' : 'example@email.com'}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-slate-900 font-medium text-sm">
                  {t('contactPhone')}
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-white border-amber-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 h-12"
                  placeholder="+971 XX XXX XXXX"
                />
              </div>

              <div className="space-y-2">
                <label className="text-slate-900 font-medium text-sm">
                  {t('contactMessage')}
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="bg-white border-amber-300 text-slate-900 placeholder:text-slate-400 focus:border-amber-500 resize-none"
                  placeholder={t('language') === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold py-6 text-lg shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('language') === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    {t('contactButton')}
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
